import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Delete as DeleteIcon } from '@mui/icons-material'
import {
    Autocomplete,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { gql } from '__generated__'
import {
    AddWorkExperienceInput,
    GetCompanyOfficesQuery,
    Industries,
    Sectors,
    Titles,
    WorkExperienceEntry,
} from '__generated__/graphql'
import {
    GetCandidateWorkExperienceOptions,
    GetCompaniesById,
} from 'candidate/graphql/generatedProfile.gql'
import { format, parse } from 'date-fns'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { useParams } from 'react-router-dom'
import { GetCompanyOffices } from 'recruiter/graphql/companies.gql'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import DrawerComponent from 'shared/components/DrawerComponent'
import NonFormikPhoneInput from 'shared/components/NonFormikPhoneInput'
import useToast from 'shared/components/Toast/useToast'
import { DropdownOption } from 'shared/models/dropdownOption'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'
import {
    CandidateWorkExperience,
    GetCandidateWorkExperience,
} from './CandidateExperiencePanel'

export const AddCandidateWorkExperience = gql(/* GraphQL */ `
    mutation AddCandidateWorkExperience(
        $candidateId: ID!
        $workExperience: AddWorkExperienceInput!
    ) {
        addCandidateWorkExperience(
            candidateId: $candidateId
            workExperience: $workExperience
        ) {
            message
            success
        }
    }
`)

export const UpdateCandidateWorkExperience = gql(/* GraphQL */ `
    mutation UpdateCandidateWorkExperience(
        $candidateWorkExperienceId: ID!
        $workExperience: WorkExperienceEntry!
    ) {
        updateCandidateWorkExperience(
            candidateWorkExperienceId: $candidateWorkExperienceId
            workExperience: $workExperience
        ) {
            success
            message
        }
    }
`)

export const DeleteCandidateWorkExperience = gql(/* GraphQL */ `
    mutation DeleteWorkExperience($candidateWorkExperienceId: ID!) {
        deleteWorkExperience(
            candidateWorkExperienceId: $candidateWorkExperienceId
        ) {
            message
            success
        }
    }
`)

/** A type for the work experience form that includes a small piece of metadata to determine which response shape to expect. */
type FormValues =
    | ({ operation: 'add' } & AddWorkExperienceInput)
    | ({ operation: 'update' } & WorkExperienceEntry)

interface CandidateExperienceDrawerProps {
    open: boolean
    onClose: () => void
    handleCancel: () => void
    workExperience: CandidateWorkExperience[number] | null
}

export const CandidateExperienceDrawer = ({
    open,
    onClose,
    handleCancel,
    workExperience,
}: CandidateExperienceDrawerProps) => {
    const { candidateId } = useParams()
    const [industries, setIndustries] = useState<DropdownOption<Industries>[]>(
        []
    )
    const [titles, setTitles] = useState<DropdownOption<Titles>[]>([])
    const [sectors, setSectors] = useState<DropdownOption<Sectors>[]>([])
    const [officeLocations, setOfficeLocations] = useState<
        NonNullable<GetCompanyOfficesQuery['company']['companyOffices']>
    >([])
    const toast = useToast()

    const validationSchema = Yup.object({
        companyId: Yup.string().required('Company is required'),
        companyOfficeId: Yup.string().nullable(),
        titleId: Yup.string().required('Position is required'),
        industryIds: Yup.array().nullable(),
        sectorIds: Yup.array().nullable(),
        experienceDescription: Yup.string(),
        workEmail: Yup.string().email('Invalid email address').nullable(),
        workPhone: Yup.string().nullable(),
    })

    const initialValues: FormValues = workExperience
        ? {
              operation: 'update',
              isCurrent: workExperience.isCurrent,
              companyId: workExperience.company?.id ?? '',
              companyOfficeId: workExperience.companyOffice?.id ?? null,
              endDate: workExperience.endDate ?? null,
              experienceDescription: workExperience.experienceDescription ?? '',
              industryIds:
                  workExperience.industries?.map(
                      ({ industry }) => industry.id
                  ) ?? [],
              sectorIds:
                  workExperience.sectors?.map(({ sector }) => sector.id) ?? [],
              startDate: workExperience.startDate ?? '',
              titleId: workExperience.title?.id ?? '',
              workEmail: workExperience.workEmail ?? null,
              workPhone: workExperience.workPhone ?? null,
          }
        : {
              operation: 'add',
              isCurrent: false,
              companyId: '',
              companyOfficeId: null,
              endDate: null,
              experienceDescription: '',
              industryIds: [],
              sectorIds: [],
              startDate: '',
              titleId: '',
              workEmail: null,
              workPhone: null,
          }

    useQuery(GetCandidateWorkExperienceOptions, {
        onCompleted: (data) => {
            setIndustries(data.industries)
            setTitles(data.titles)
            setSectors(data.sectors)
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to fetch candidate work experience options'
                ),
            })
        },
    })

    const [getCompanyOffices, { loading: isOfficeLocationDataLoading }] =
        useLazyQuery(GetCompanyOffices, {
            onCompleted: (data) => {
                setOfficeLocations(data.company.companyOffices ?? [])
            },
        })

    useEffect(() => {
        if (workExperience?.company?.id) {
            getCompanyOffices({
                variables: {
                    companyId: workExperience.company.id,
                },
            })
        }
    }, [workExperience, getCompanyOffices])

    const handleClose = () => {
        handleCancel()
        onClose()
    }

    const [addWorkExperience] = useMutation(AddCandidateWorkExperience, {
        refetchQueries: [GetCandidateWorkExperience],
        onCompleted: (data) => {
            if (data.addCandidateWorkExperience.success) {
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Candidate work experience added successfully',
                })

                handleCancel()
            } else {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: `Failed to add candidate work experience: ${data.addCandidateWorkExperience.message}`,
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to add candidate work experience'
                ),
            })
        },
    })

    const [updateWorkExperience] = useMutation(UpdateCandidateWorkExperience, {
        refetchQueries: [GetCandidateWorkExperience],
        onCompleted: (data) => {
            if (data.updateCandidateWorkExperience.success) {
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Candidate work experience updated successfully',
                })

                handleCancel()
            } else {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: `Failed to update candidate work experience: ${data.updateCandidateWorkExperience.message}`,
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to update candidate work experience'
                ),
            })
        },
    })

    const [deleteWorkExperience] = useMutation(DeleteCandidateWorkExperience, {
        refetchQueries: [GetCandidateWorkExperience],
        onCompleted: (data) => {
            if (data.deleteWorkExperience.success) {
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Candidate work experience deleted successfully',
                })

                handleCancel()
            } else {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: `Failed to delete candidate work experience: ${data.deleteWorkExperience.message}`,
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to delete candidate work experience'
                ),
            })
        },
    })

    const handleAddWorkExperience = (formValues: AddWorkExperienceInput) => {
        addWorkExperience({
            variables: {
                candidateId: candidateId ?? '',
                workExperience: formValues,
            },
        })
    }

    const handleUpdateWorkExperience = (formValues: WorkExperienceEntry) => {
        updateWorkExperience({
            variables: {
                candidateWorkExperienceId: workExperience?.id ?? '',
                workExperience: formValues,
            },
        })
    }

    const handleDeleteWorkExperience = () => {
        deleteWorkExperience({
            variables: { candidateWorkExperienceId: workExperience?.id ?? '' },
        })
    }

    const { values, setFieldValue, errors, submitForm, handleBlur, touched } =
        useFormik<FormValues>({
            initialValues: initialValues,
            validationSchema,
            onSubmit: (values) => {
                if (values.operation === 'update') {
                    const { operation, ...workExperience } = values
                    handleUpdateWorkExperience({
                        ...workExperience,
                        endDate: values.isCurrent ? null : values.endDate,
                    })
                } else {
                    const { operation, ...workExperience } = values
                    handleAddWorkExperience({
                        ...workExperience,
                        endDate: values.isCurrent ? null : values.endDate,
                    })
                }
            },
            enableReinitialize: true,
        })

    const endDateValue = useMemo(() => {
        if (values.isCurrent || !values.endDate) return null
        return parse(values.endDate, 'MM-yyyy', new Date())
    }, [values.endDate, values.isCurrent])

    const { data: companiesById, loading } = useQuery(GetCompaniesById, {
        variables: {
            ids: values.companyId ? [values.companyId] : [],
        },
    })

    return (
        <DrawerComponent
            open={open}
            anchor="right"
            onClose={handleClose}
            hasCancelButton
            onCancel={handleClose}
            primaryActionText={workExperience ? 'Update' : 'Add'}
            onPrimaryAction={submitForm}
            headerTitle="Experience"
        >
            <Grid container spacing={3}>
                {/* Start Date */}
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <DatePicker
                            format="MM-yyyy"
                            value={
                                values.startDate
                                    ? parse(
                                          values.startDate,
                                          'MM-yyyy',
                                          new Date()
                                      )
                                    : null
                            }
                            onChange={(value) => {
                                if (value && !isNaN(value.getTime())) {
                                    setFieldValue(
                                        'startDate',
                                        format(value, 'MM-yyyy')
                                    )
                                }
                            }}
                            label="Start Date"
                            views={['year', 'month']}
                        />
                    </FormControl>
                </Grid>

                {/* End Date */}
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <DatePicker
                            format="MM-yyyy"
                            value={endDateValue}
                            onChange={(value) => {
                                if (value && !isNaN(value.getTime())) {
                                    setFieldValue(
                                        'endDate',
                                        format(value, 'MM-yyyy')
                                    )
                                    setFieldValue('isCurrent', false)
                                }
                            }}
                            label="End Date"
                            views={['year', 'month']}
                        />
                    </FormControl>
                </Grid>

                {/* isCurrent */}
                <Grid item xs={12} style={{ paddingTop: getPxAsRem(4) }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                onChange={(e) => {
                                    setFieldValue('isCurrent', e.target.checked)
                                }}
                                checked={values.isCurrent || false}
                            />
                        }
                        label="Is current work experience?"
                    />
                </Grid>

                {/* Company */}
                <Grid item xs={12}>
                    <CompaniesAutocomplete
                        value={companiesById?.companiesById[0] ?? null}
                        loading={loading}
                        error={touched.companyId && Boolean(errors.companyId)}
                        helperText={
                            touched.companyId &&
                            typeof errors.companyId === 'string'
                                ? errors.companyId
                                : undefined
                        }
                        onChange={(_, newValue) => {
                            // this value will never be an array because the Autocomplete component is not set to allow multiple selections in this case but we need to check for it to satisfy TypeScript
                            if (Array.isArray(newValue)) return
                            const selectedCompanyId =
                                typeof newValue === 'string'
                                    ? newValue
                                    : newValue?.id || ''
                            setFieldValue('companyId', selectedCompanyId)

                            // Fetch locations when a company is selected
                            if (selectedCompanyId) {
                                getCompanyOffices({
                                    variables: {
                                        companyId: selectedCompanyId,
                                    },
                                })
                            } else {
                                setOfficeLocations([])
                            }
                        }}
                    />
                </Grid>

                {/* Location */}
                <Grid item xs={12}>
                    <Autocomplete
                        fullWidth
                        options={officeLocations.map(({ officeLocation }) => ({
                            id: officeLocation?.id ?? '',
                            value: officeLocation?.value ?? '',
                        }))}
                        getOptionLabel={(option) => option.value}
                        value={
                            officeLocations.find(
                                (option) => option.id === values.companyOfficeId
                            )?.officeLocation || null
                        }
                        onChange={(_, newValue) =>
                            setFieldValue(
                                'companyOfficeId',
                                officeLocations.find(
                                    ({ officeLocation }) =>
                                        officeLocation?.id === newValue?.id
                                )?.id || null
                            )
                        }
                        disabled={
                            isOfficeLocationDataLoading || !values.companyId
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Location"
                                variant="outlined"
                                error={
                                    touched.companyOfficeId &&
                                    Boolean(errors.companyOfficeId)
                                }
                                helperText={
                                    touched.companyOfficeId &&
                                    typeof errors.companyOfficeId === 'string'
                                        ? errors.companyOfficeId
                                        : undefined
                                }
                            />
                        )}
                    />
                </Grid>

                {/* Position */}
                <Grid item xs={12}>
                    <Autocomplete
                        fullWidth
                        options={titles}
                        getOptionLabel={(option) => option.value}
                        value={
                            titles.find(
                                (title) => title.id === values.titleId
                            ) || null
                        }
                        onChange={(event, newValue) =>
                            setFieldValue('titleId', newValue?.id || '')
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Position"
                                variant="outlined"
                                error={
                                    touched.titleId && Boolean(errors.titleId)
                                }
                                helperText={
                                    touched.titleId &&
                                    typeof errors.titleId === 'string'
                                        ? errors.titleId
                                        : undefined
                                }
                            />
                        )}
                    />
                </Grid>

                {/* Industries */}
                <Grid item xs={12}>
                    <Autocomplete
                        fullWidth
                        multiple
                        options={industries}
                        getOptionLabel={(option) => option.value}
                        value={industries.filter((industry) =>
                            values.industryIds?.includes(industry.id)
                        )}
                        onChange={(event, newValue) =>
                            setFieldValue(
                                'industryIds',
                                newValue.map((industry) => industry.id)
                            )
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Industry"
                                variant="outlined"
                                error={
                                    touched.industryIds &&
                                    Boolean(errors.industryIds)
                                }
                                helperText={
                                    touched.industryIds &&
                                    typeof errors.industryIds === 'string'
                                        ? errors.industryIds
                                        : undefined
                                }
                            />
                        )}
                    />
                </Grid>

                {/* Sectors */}
                <Grid item xs={12}>
                    <Autocomplete
                        fullWidth
                        multiple
                        options={sectors}
                        getOptionLabel={(option) => option.value}
                        value={sectors.filter((sector) =>
                            values.sectorIds?.includes(sector.id)
                        )}
                        onChange={(event, newValue) =>
                            setFieldValue(
                                'sectorIds',
                                newValue.map((sector) => sector.id)
                            )
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Sector"
                                variant="outlined"
                                error={
                                    touched.sectorIds &&
                                    Boolean(errors.sectorIds)
                                }
                                helperText={
                                    touched.sectorIds &&
                                    typeof errors.sectorIds === 'string'
                                        ? errors.sectorIds
                                        : undefined
                                }
                            />
                        )}
                    />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        label="Sector Description"
                        name="experienceDescription"
                        value={values.experienceDescription}
                        onChange={(e) =>
                            setFieldValue(
                                'experienceDescription',
                                e.target.value
                            )
                        }
                        onBlur={handleBlur}
                        error={
                            touched.experienceDescription &&
                            Boolean(errors.experienceDescription)
                        }
                        helperText={
                            touched.experienceDescription &&
                            typeof errors.experienceDescription === 'string'
                                ? errors.experienceDescription
                                : undefined
                        }
                    />
                </Grid>

                {/* Personal Email */}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Work Email"
                        name="workEmail"
                        value={values.workEmail}
                        onChange={(e) =>
                            setFieldValue('workEmail', e.target.value)
                        }
                        onBlur={handleBlur}
                        error={touched.workEmail && Boolean(errors.workEmail)}
                        helperText={
                            touched.workEmail &&
                            typeof errors.workEmail === 'string'
                                ? errors.workEmail
                                : undefined
                        }
                    />
                </Grid>

                {/* Phone Input */}
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <NonFormikPhoneInput
                            id="workPhone"
                            size="small"
                            value={values.workPhone || ''}
                            error={
                                !!values.workPhone &&
                                !isValidPhoneNumber(values.workPhone)
                            }
                            helperText={
                                !!values.workPhone &&
                                !isValidPhoneNumber(values.workPhone)
                                    ? 'Please provide valid phone no.'
                                    : ''
                            }
                            label="Phone"
                            handleInputChange={(value) => {
                                setFieldValue('workPhone', value || '')
                            }}
                        />
                    </FormControl>
                </Grid>

                {/* Delete Button */}
                {workExperience && (
                    <Grid item xs={12}>
                        <Button
                            startIcon={<DeleteIcon />}
                            variant="text"
                            color="error"
                            onClick={handleDeleteWorkExperience}
                        >
                            Delete Work Experience
                        </Button>
                    </Grid>
                )}
            </Grid>
        </DrawerComponent>
    )
}
